import type { WorkItem, WorkItemKind } from "@api/generated/model";
import { HrefLink } from "@app/links";
import { useRecent } from "@app/workspace";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ago } from "./format";

/** What a page shows before anything is chosen (docs/ui-design.md): a question, one large picker and ways in (recent
 *  items, this season's list). The page's toolbars, tabs and side panels are not rendered until a choice is made. */
export function StartCard({ title, description, children }: { title: string; description?: ReactNode; children: ReactNode }) {
  return (
    <section className="card start-card">
      <div className="stack" style={{ gap: 4 }}>
        <h2 className="h2">{title}</h2>
        {description && <p className="mu">{description}</p>}
      </div>
      {children}
    </section>
  );
}

const KIND_CHIP: Record<WorkItemKind, string> = { plot: "b", genotype: "g", trial: "b", partner: "", query: "", page: "" };

/** One recently opened item as a row (the home page's Folytatás, a start card): what it is, its title, when. */
export function RecentRow({ item, showKind }: { item: WorkItem; showKind?: boolean }) {
  const { t } = useTranslation();
  return (
    <HrefLink href={item.href} className="row-link">
      {showKind && <span className={`chip ${KIND_CHIP[item.kind]}`}>{t(`palette.kind.${item.kind}`)}</span>}
      <span className="grow stack">
        <b style={{ fontWeight: 600, fontSize: "0.8438rem" }}>{item.title}</b>
        {item.subtitle && <span className="sm mu">{item.subtitle}</span>}
      </span>
      <span className="sm mu">{ago(item.at)}</span>
    </HrefLink>
  );
}

/** The user's recently opened items of a kind (from their settings); nothing when there are none. ``hrefOf``: where an
 *  item opens on this page (a line seen on its sheet, opened in the register). */
export function RecentItems({
  kind,
  max = 3,
  filter,
  hrefOf,
}: {
  kind: WorkItemKind;
  max?: number;
  filter?: (item: WorkItem) => boolean;
  hrefOf?: (item: WorkItem) => string | null;
}) {
  const { t } = useTranslation();
  const items = useRecent()
    .recent.flatMap((r) => {
      if (r.kind !== kind || (filter && !filter(r))) return [];
      const href = hrefOf ? hrefOf(r) : r.href;
      return href ? [{ ...r, href }] : [];
    })
    .filter((r, i, all) => all.findIndex((x) => x.href === r.href) === i) // a line's sheet and tree can lead to one place
    .slice(0, max);
  if (!items.length) return null;
  return (
    <div className="stack" style={{ gap: 4 }}>
      <span className="sec">{t("home.recentWork")}</span>
      {items.map((r) => (
        <RecentRow key={r.href} item={r} />
      ))}
    </div>
  );
}
